import {
  $, S, api, toast, store, load, setUnauthorizedHandler, on, emit, errMsg, haptic,
  setStorageUser, hydrateUserLocalState, purgeLegacySharedStorage, formSheet,
} from './core.js';
import {
  initEditor, restoreTabs, saveTab, applyPrefs, renderTabs, E, updateMobileFileBar,
} from './editor.js';
import {
  resetFiles, setupDrop, setupFilesExplorer, setupFilesPullRefresh, connectEvents, newItem, renderTree,
} from './files.js';
import { loadRepos } from './git.js';
import {
  buildTermKeys, ensureTerminals, fitActiveTerm, focusActiveTerm, newTerm, resetTerminals, preloadXterm,
} from './term.js';
import { refreshAiReady } from './ai.js';
import { refreshOpencode } from './opencode.js';
import { initAgentsUi, onWorkspaceAgents } from './agents.js';
import { initEditorDiff } from './editor-diff.js';
import {
  settingsSheet, findFileByName, searchInFiles, workspaceSheet, connectLastSsh, refreshSshLastButton,
} from './manage.js';
import {
  initBottomNav, syncBottomNav, bootViewFromStore, updateExplorerProject,
} from './nav-shell.js';
import { needsOnboarding, runOnboarding } from './onboarding.js';
import { initMobileAiSplit, syncMobileSplitForView } from './mobile-ai-split.js';
import { initViewA11y, syncAuthA11y, syncViewA11y } from './view-a11y.js';
import { syncAppBuildCache } from './cache-sync.js';
import { initDesktopMobileGate } from './desktop-mobile-gate.js';
import { initAdminConsole } from './admin-console.js';
import { startPresenceHeartbeat } from './presence.js';
import { initPwaInstallFromQuery, initPwaInstallListeners, registerPwaServiceWorker } from './pwa-install.js';

const OAUTH_ERR = {
  oauth: 'فشل تسجيل Google — حاول مرة أخرى',
  state: 'انتهت الجلسة — أعد المحاولة',
  oauth_disabled: 'Google OAuth غير مفعّل على السيرفر',
};

function showLogin(message = '') {
  S.user = null;
  syncAuthA11y(false);
  const login = $('#login');
  if (login) {
    login.hidden = false;
    login.removeAttribute('hidden');
  }
  const err = $('#login-err');
  if (err) {
    let text = '';
    if (typeof message === 'string') text = message;
    else if (message && typeof message === 'object') {
      text = message.error
        || (message.code === 'SESSION_SUPERSEDED'
          ? 'تم تسجيل الدخول من جهاز آخر — سجّل الدخول مجدداً'
          : 'انتهت الجلسة — سجّل الدخول');
    }
    err.textContent = text;
  }
  initGoogleLogin();
  initOmdaaSso();
  initLandingAuth();
  const lastEmail = load('me.lastEmail', '');
  const emailInp = $('#login-email');
  if (emailInp && lastEmail && !emailInp.value) emailInp.value = lastEmail;
  setTimeout(() => (emailInp?.value ? $('#login-pw') : emailInp)?.focus(), 50);
}

let loginMode = 'login';

function syncLoginSocialVisibility() {
  const social = $('#login-social');
  const or = $('#login-or');
  const google = $('#login-google');
  const sso = $('#login-omdaa-sso');
  const any = !!(google && !google.hidden) || !!(sso && !sso.hidden);
  if (social) social.hidden = !any;
  if (or) {
    or.hidden = !any;
    or.setAttribute('aria-hidden', any ? 'false' : 'true');
  }
}

function setLoginMode(mode) {
  loginMode = mode === 'signup' ? 'signup' : 'login';
  const isSignup = loginMode === 'signup';
  const pw2 = $('#login-pw2');
  const pw2Wrap = $('#login-pw2-wrap');
  const submit = $('#login-submit');
  const pw = $('#login-pw');
  const sub = $('#login-card-sub');
  const socialTitle = $('#login-social-title');
  const socialDesc = $('#login-social-desc');
  const orText = $('#login-or-text');
  const switchText = $('#login-switch-text');
  const switchBtn = $('#login-switch-btn');

  for (const t of document.querySelectorAll('.login-tab')) {
    const on = t.dataset.mode === loginMode;
    t.classList.toggle('on', on);
    t.setAttribute('aria-selected', on ? 'true' : 'false');
  }
  if (pw2Wrap) pw2Wrap.hidden = !isSignup;
  if (pw2) {
    pw2.hidden = !isSignup;
    pw2.required = isSignup;
    if (!isSignup) pw2.value = '';
  }
  if (submit) submit.textContent = isSignup ? 'إنشاء حساب' : 'تسجيل الدخول';
  const forgot = $('#login-forgot-btn');
  if (forgot) forgot.hidden = isSignup;
  if (pw) pw.autocomplete = isSignup ? 'new-password' : 'current-password';
  if (sub) {
    const icon = sub.querySelector('svg');
    sub.textContent = '';
    if (icon) sub.appendChild(icon);
    sub.appendChild(document.createTextNode(isSignup ? 'إنشاء حساب جديد في Omdaa Code' : 'تسجيل الدخول إلى حسابك'));
  }
  if (socialTitle) {
    socialTitle.textContent = isSignup
      ? 'أنشئ حسابك بسرعة — الطريقة المفضلة'
      : 'سجّل الدخول بسرعة — الطريقة المفضلة';
  }
  if (socialDesc) {
    socialDesc.textContent = isSignup
      ? 'استخدم حساب Omdaa أو Google للتسجيل فوراً. آمن وسريع.'
      : 'استخدم حساب Omdaa أو Google للدخول فوراً. آمن وسريع.';
  }
  if (orText) orText.textContent = isSignup ? 'أو سجّل بالبريد' : 'أو سجّل الدخول بالبريد';
  if (switchText) switchText.textContent = isSignup ? 'لديك حساب بالفعل؟' : 'ليس لديك حساب؟';
  if (switchBtn) {
    switchBtn.textContent = isSignup ? 'تسجيل الدخول' : 'إنشاء حساب جديد';
    switchBtn.dataset.to = isSignup ? 'login' : 'signup';
  }
}

function initLandingAuth() {
  const tabSignup = $('#login-tab-signup');
  const switchEl = $('#login-switch');
  const tabs = document.querySelector('#login .login-tabs');
  const on = !!S.features?.signup;
  if (tabSignup) tabSignup.hidden = !on;
  if (switchEl) switchEl.hidden = !on;
  if (tabs) tabs.hidden = !on;
  if (!on && loginMode === 'signup') setLoginMode('login');
}

for (const t of document.querySelectorAll('.login-tab')) {
  t.addEventListener('click', () => setLoginMode(t.dataset.mode));
}

$('#login-switch-btn')?.addEventListener('click', () => {
  const to = $('#login-switch-btn')?.dataset?.to || 'signup';
  setLoginMode(to);
});

$('#login-forgot-btn')?.addEventListener('click', async () => {
  const err = $('#login-err');
  if (err) err.textContent = '';
  const email = ($('#login-email')?.value || '').trim();
  if (!email.includes('@')) {
    if (err) err.textContent = 'أدخل بريدك الإلكتروني أولاً';
    $('#login-email')?.focus();
    return;
  }
  try {
    const r = await api('/forgot-password', { method: 'POST', body: { email } });
    toast(r.message || 'تحقق من بريدك', 'ok');
  } catch (ex) {
    if (err) err.textContent = errMsg(ex);
  }
});

$('#login-pw-toggle')?.addEventListener('click', () => {
  const pw = $('#login-pw');
  const btn = $('#login-pw-toggle');
  if (!pw || !btn) return;
  const show = pw.type === 'password';
  pw.type = show ? 'text' : 'password';
  btn.setAttribute('aria-label', show ? 'إخفاء كلمة المرور' : 'إظهار كلمة المرور');
  const eyeOn = btn.querySelector('.login-eye-on');
  const eyeOff = btn.querySelector('.login-eye-off');
  if (eyeOn) eyeOn.hidden = show;
  if (eyeOff) eyeOff.hidden = !show;
});

async function initGoogleLogin() {
  const btn = $('#login-google');
  if (!btn) return;
  try {
    const s = await api('/auth/google/status');
    btn.hidden = !s.enabled;
  } catch {
    btn.hidden = true;
  }
  syncLoginSocialVisibility();
}

async function initOmdaaSso() {
  const btn = $('#login-omdaa-sso');
  if (!btn) return;
  try {
    const s = await api('/sso/status');
    const on = !!(s.enabled || s.oidc?.enabled);
    btn.hidden = !on;
    if (s.bridgeUrl) btn.dataset.bridge = s.bridgeUrl;
    if (s.preferOidc) btn.dataset.preferOidc = '1';
  } catch {
    btn.hidden = true;
  }
  syncLoginSocialVisibility();
}

$('#login-omdaa-sso')?.addEventListener('click', () => {
  const err = $('#login-err');
  if (err) err.textContent = '';
  const btn = $('#login-omdaa-sso');
  const bridge = btn?.dataset?.bridge || '/api/auth/omdaa/start';
  window.location.href = bridge;
});

$('#login-google')?.addEventListener('click', async () => {
  const err = $('#login-err');
  err.textContent = '';
  try {
    const { url } = await api('/auth/google/start', { method: 'POST', body: {} });
    if (url) window.location.href = url;
  } catch (ex) {
    err.textContent = errMsg(ex);
  }
});

setUnauthorizedHandler(showLogin);

async function afterAuth(me) {
  $('#login-pw').value = '';
  $('#login-pw2').value = '';
  const errEl = $('#login-err');
  if (errEl) errEl.textContent = '';
  S.user = me?.user || null;
  setStorageUser(me?.user || null);
  purgeLegacySharedStorage();
  hydrateUserLocalState();
  if (me?.user?.email) store('me.lastEmail', me.user.email);
  S.workspaces = me?.workspaces || [];
  S.features = { ...S.features, ...(me?.features || {}) };
  S.billing = me?.billing || S.billing;
  syncAuthA11y(true);
  try {
    await boot(me);
  } catch (ex) {
    console.error(ex);
    toast('تحذير: ' + errMsg(ex), 'err', 4500);
    document.body.dataset.view = 'agents';
  }
}

$('#login-form')?.addEventListener('submit', async (e) => {
  e.preventDefault();
  const err = $('#login-err');
  err.textContent = '';
  const email = $('#login-email')?.value?.trim() || '';
  const password = $('#login-pw')?.value || '';
  const password2 = $('#login-pw2')?.value || '';
  try {
    if (loginMode === 'signup') {
      if (password !== password2) { err.textContent = 'كلمتا السر غير متطابقتين'; return; }
      await afterAuth(await api('/signup', { method: 'POST', body: { email, password } }));
      toast('تم إنشاء حسابك', 'ok');
      return;
    }
    await afterAuth(await api('/login', { method: 'POST', body: { email, password } }));
  } catch (ex) {
    err.textContent = errMsg(ex);
    /* حساب Google — أبرز زر الدخول عبر Google */
    if (ex && (ex.data?.code === 'USE_GOOGLE' || /Google/i.test(String(ex.message || '')))) {
      const g = $('#login-google');
      if (g) {
        g.classList.add('pulse-hint');
        g.focus();
        setTimeout(() => g.classList.remove('pulse-hint'), 2600);
      }
    }
  }
});

function show(name) {
  document.body.dataset.view = name;
  syncBottomNav(name);
  syncViewA11y(name);
  syncMobileSplitForView(name);
  store('me.view', name);
  haptic(6);
  if (name === 'term') {
    void ensureTerminals().then(() => focusActiveTerm());
  }
  if (name === 'git') loadRepos();
  if (name === 'editor') {
    if (E.view) setTimeout(() => E.view.requestMeasure(), 50);
    import('./editor.js').then(({ renderTabs, curTab }) => {
      renderTabs();
      if (curTab()) setTimeout(() => E.view?.focus(), 60);
    });
  }
  if (name === 'files') {
    updateExplorerProject();
    renderTree();
  }
  if (name === 'agents') {
    renderAgentsView();
    setTimeout(() => $('#agents-input')?.focus(), 120);
  }
  fitActiveTerm();
}

function renderAgentsView() {
  import('./agents.js').then((m) => m.renderAgents());
}

on('show', (name) => show(name));

initBottomNav((name) => emit('show', name));
$('#btn-save')?.addEventListener('click', () => saveTab());
$('#btn-more')?.addEventListener('click', () => settingsSheet());
$('#btn-ws')?.addEventListener('click', () => workspaceSheet());
$('#btn-ws-chip')?.addEventListener('click', () => workspaceSheet());
$('#btn-ssh-last')?.addEventListener('click', () => connectLastSsh());
$('#empty-open-files')?.addEventListener('click', () => emit('show', 'files'));
$('#empty-new-file')?.addEventListener('click', () => {
  emit('show', 'files');
  newItem('file');
});
on('tabs', () => updateMobileFileBar());
$('#term-new')?.addEventListener('click', async () => {
  if (document.querySelectorAll('#term-tabs .ttab').length >= 8) toast('الحد 8 طرفيات', 'err');
  else await newTerm();
});

$('#f-newfile')?.addEventListener('click', () => newItem('file'));
$('#f-newdir')?.addEventListener('click', () => newItem('dir'));
$('#f-refresh')?.addEventListener('click', () => { resetFiles(); toast('تم التحديث', 'ok'); });
$('#f-find')?.addEventListener('click', () => findFileByName());
$('#f-rg')?.addEventListener('click', () => searchInFiles());

const app = $('#app');
function fitViewport() {
  const v = window.visualViewport;
  if (!v || !app) return;
  app.style.height = v.height + 'px';
  app.style.transform = v.offsetTop ? `translateY(${v.offsetTop}px)` : '';
  document.body.classList.toggle('kb', window.innerHeight - v.height > 120);
  fitActiveTerm();
}
if (window.visualViewport) {
  window.visualViewport.addEventListener('resize', fitViewport);
  window.visualViewport.addEventListener('scroll', fitViewport);
}
window.addEventListener('resize', () => { fitViewport(); fitActiveTerm(); });
if (window.ResizeObserver && $('#term-host')) new ResizeObserver(() => fitActiveTerm()).observe($('#term-host'));

document.addEventListener('workspace-changed', async () => {
  resetTerminals();
  onWorkspaceAgents();
  await restoreTabs(load('me.tabs', { tabs: [], active: null }));
  void ensureTerminals().then(() => {
    if (document.body.dataset.view === 'term') focusActiveTerm();
    else fitActiveTerm();
  });
});

/** تحميل xterm + جلسة WS بعد الدخول (موبايل + ديسكتوب) */
function preloadTerminalsAfterBoot() {
  void preloadXterm().catch(() => {});
  buildTermKeys();
  const run = () => {
    void ensureTerminals()
      .then(() => fitActiveTerm())
      .catch((e) => console.warn('[term] boot preload', e));
  };
  if (typeof requestIdleCallback === 'function') requestIdleCallback(run, { timeout: 2500 });
  else setTimeout(run, 400);
}

let booted = false;
async function boot(me) {
  if (me) {
    S.user = me.user;
    setStorageUser(me.user);
    purgeLegacySharedStorage();
    hydrateUserLocalState();
    S.workspaces = me.workspaces;
    S.features = me.features;
    S.billing = me.billing || S.billing;
    S.app = me.app || S.app;
    document.title = S.app;
    const w0 = load('me.w', 'local');
    S.w = S.workspaces.some((w) => w.id === w0) ? w0 : (S.workspaces[0]?.id || 'local');
    store('me.w', S.w);
    const wsel = $('#wsel');
    const wnm = S.workspaces.find((w) => w.id === S.w)?.name || S.w;
    if (wsel) { wsel.textContent = wnm; wsel.title = wnm; }
  }
  if (booted) {
    /* تبديل حساب على نفس الجلسة — أعد تحميل حالة العميل لهذا المستخدم */
    const { resetAgentsForUser } = await import('./agents.js');
    resetAgentsForUser?.();
    await resetFiles();
    await restoreTabs(load('me.tabs', { tabs: [], active: null }));
    if (needsOnboarding(S.user)) {
      const result = await runOnboarding();
      if (result?.user) S.user = { ...S.user, ...result.user };
      emit('show', result?.home || bootViewFromStore());
    }
    return;
  }
  booted = true;

  document.documentElement.style.setProperty('--fs', S.settings.font + 'px');
  initEditor();
  buildTermKeys();
  applyPrefs();
  setupDrop();
  setupFilesExplorer();
  setupFilesPullRefresh();
  initAgentsUi();
  initEditorDiff();
  initMobileAiSplit();
  initViewA11y();
  if (S.user?.role === 'admin') initAdminConsole();
  startPresenceHeartbeat();
  fitViewport();
  connectEvents();
  initClientErrorReporter();
  await Promise.all([
    refreshAiReady(),
    refreshOpencode(),
    resetFiles(),
    loadRepos(),
  ]);
  await restoreTabs(load('me.tabs', { tabs: [], active: null }));
  refreshSshLastButton();

  updateExplorerProject();
  void preloadTerminalsAfterBoot();
  if (needsOnboarding(S.user)) {
    const result = await runOnboarding();
    if (result?.user) S.user = { ...S.user, ...result.user };
    emit('show', result?.home || bootViewFromStore());
  } else emit('show', bootViewFromStore());
}

/** إرسال أخطاء الواجهة للسيرفر → لوحة الإدارة */
function initClientErrorReporter() {
  if (window.__omdaaErrRep) return;
  window.__omdaaErrRep = true;
  const send = (payload) => {
    try {
      const body = JSON.stringify({
        ...payload,
        view: document.body?.dataset?.view || null,
        href: location.href.slice(0, 300),
        ua: navigator.userAgent.slice(0, 180),
      });
      fetch('/api/client-error', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'me' },
        body,
        credentials: 'same-origin',
        keepalive: true,
      }).catch(() => {
        try {
          if (navigator.sendBeacon) {
            navigator.sendBeacon('/api/client-error', new Blob([body], { type: 'application/json' }));
          }
        } catch { /* ignore */ }
      });
    } catch { /* ignore */ }
  };
  window.addEventListener('error', (ev) => {
    send({
      source: 'window.onerror',
      message: String(ev.message || ev.error?.message || 'error').slice(0, 500),
      stack: String(ev.error?.stack || '').slice(0, 4000),
      file: ev.filename || null,
      line: ev.lineno || null,
      col: ev.colno || null,
    });
  });
  window.addEventListener('unhandledrejection', (ev) => {
    const r = ev.reason;
    send({
      source: 'unhandledrejection',
      message: String(r?.message || r || 'rejection').slice(0, 500),
      stack: String(r?.stack || '').slice(0, 4000),
    });
  });
}

(async () => {
  if (initDesktopMobileGate()) return;

  initPwaInstallListeners();
  void registerPwaServiceWorker();

  await syncAppBuildCache();
  initPwaInstallFromQuery();
  try {
    const pub = await fetch('/api/public', { cache: 'no-store' }).then((r) => r.json());
    S.features = { signup: pub.signup, googleLogin: pub.googleLogin, maxAiAgents: pub.maxAiAgents };
    initLandingAuth();
  } catch { S.features = S.features || {}; }

  const q = new URLSearchParams(location.search);
  const oauthErr = q.get('error');
  const billingNote = q.get('billing');
  const verifyTok = q.get('verify');
  const secureResetTok = q.get('secureReset');
  const passwordResetTok = q.get('passwordReset');
  if (verifyTok) {
    history.replaceState(null, '', location.pathname);
    try {
      await api(`/account/verify?token=${encodeURIComponent(verifyTok)}`);
      toast('تم توثيق بريدك بنجاح', 'ok');
    } catch (ex) {
      toast(errMsg(ex), 'err');
    }
  }
  async function promptNewPassword(token, intro, okMsg) {
    history.replaceState(null, '', location.pathname);
    showLogin(intro);
    try {
      const v = await formSheet('كلمة مرور جديدة', [
        { key: 'next', label: 'كلمة مرور جديدة (8+)', type: 'password' },
        { key: 'confirm', label: 'تأكيد كلمة المرور', type: 'password' },
      ]);
      if (v?.next) {
        if (v.next !== v.confirm) {
          toast('كلمتا السر غير متطابقتين', 'err');
        } else {
          await api('/security/reset-password', {
            method: 'POST',
            body: { token, password: v.next },
          });
          toast(okMsg || 'تم تعيين كلمة المرور — سجّل الدخول', 'ok');
        }
      }
    } catch (ex) {
      toast(errMsg(ex), 'err');
    }
  }
  if (passwordResetTok) {
    await promptNewPassword(passwordResetTok, 'عيّن كلمة مرور جديدة لحسابك');
  } else if (secureResetTok) {
    await promptNewPassword(
      secureResetTok,
      'تم قطع كل الجلسات — عيّن كلمة مرور جديدة',
      'تم تأمين الحساب — سجّل الدخول بكلمة المرور الجديدة',
    );
  }
  if (oauthErr) {
    history.replaceState(null, '', location.pathname);
    showLogin();
    const el = $('#login-err');
    if (el) el.textContent = OAUTH_ERR[oauthErr] || OAUTH_ERR.oauth;
    return;
  }
  if (billingNote === 'success') history.replaceState(null, '', location.pathname);
  try {
    const me = await api('/me');
    S.user = me.user;
    $('#login-err') && ($('#login-err').textContent = '');
    syncAuthA11y(true);
    await boot(me);
    if (billingNote === 'success') toast('تم تفعيل Pro — شكراً!', 'ok');
  } catch (ex) {
    const msg = ex?.status === 401
      ? 'انتهت الجلسة — سجّل الدخول'
      : (ex?.message && ex.message !== 'حدث خطأ' ? ex.message : '');
    showLogin(msg);
  }
})();
